import { useState } from 'react'
import { Eye, EyeOff, KeyRound, LogOut } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { C } from '../lib/tokens'
import { Btn } from './UI'

export default function ForcePasswordReset({ email, onComplete }) {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function submit(event) {
    event.preventDefault()
    setError('')
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }

    setBusy(true)
    try {
      const { data: userData } = await supabase.auth.getUser()
      const currentMetadata = userData?.user?.user_metadata || {}
      const { error: updateError } = await supabase.auth.updateUser({
        password,
        data: {
          ...currentMetadata,
          must_reset_password: false,
          temp_password_pending_reset: false,
          password_reset_completed_at: new Date().toISOString(),
        },
      })
      if (updateError) throw updateError
      onComplete?.()
    } catch (err) {
      setError(err.message || 'Could not update your password.')
    } finally {
      setBusy(false)
    }
  }

  async function signOut() {
    await supabase.auth.signOut()
    window.location.assign('/login')
  }

  const inputStyle = {
    width: '100%',
    padding: '12px 44px 12px 14px',
    fontSize: 15,
    color: C.text,
    background: C.surface,
    border: `1px solid ${C.borderStrong}`,
    borderRadius: 10,
    boxSizing: 'border-box',
  }

  const eyeButtonStyle = {
    position: 'absolute',
    right: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 30,
    height: 30,
    border: 'none',
    borderRadius: 15,
    background: 'transparent',
    color: C.textSecondary,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: C.bg,
      padding: 24,
    }}>
      <form onSubmit={submit} style={{
        width: '100%',
        maxWidth: 430,
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 20,
        padding: 38,
        boxShadow: '0 18px 58px rgba(10, 18, 30, 0.10)',
      }}>
        <div style={{
          width: 48,
          height: 48,
          borderRadius: 24,
          background: '#EAF8F1',
          color: C.green,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 18px',
        }}>
          <KeyRound size={22} strokeWidth={2.2} />
        </div>
        <h1 style={{ margin: 0, textAlign: 'center', fontSize: 24, color: C.text, fontWeight: 820 }}>
          Create your new password
        </h1>
        <p style={{ margin: '9px 0 26px', textAlign: 'center', fontSize: 14, color: C.textSecondary, lineHeight: 1.5 }}>
          Your temporary password worked. Set a private password before entering the portal.
        </p>

        <label style={{ display: 'grid', gap: 8, marginBottom: 16, fontSize: 12, fontWeight: 720, color: C.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          New password
          <span style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={password}
              onChange={event => setPassword(event.target.value)}
              required
              minLength={8}
              autoFocus
              style={inputStyle}
            />
            <button type="button" onClick={() => setShowPassword(value => !value)} aria-label={showPassword ? 'Hide password' : 'Show password'} style={eyeButtonStyle}>
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </span>
        </label>

        <label style={{ display: 'grid', gap: 8, marginBottom: 16, fontSize: 12, fontWeight: 720, color: C.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Confirm password
          <span style={{ position: 'relative' }}>
            <input
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              value={confirm}
              onChange={event => setConfirm(event.target.value)}
              required
              minLength={8}
              style={inputStyle}
            />
            <button type="button" onClick={() => setShowConfirm(value => !value)} aria-label={showConfirm ? 'Hide password' : 'Show password'} style={eyeButtonStyle}>
              {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </span>
        </label>

        {error && (
          <div style={{ padding: 12, background: '#FDEAEA', borderRadius: 12, color: '#A6201B', fontSize: 13, marginBottom: 14 }}>
            {error}
          </div>
        )}

        <Btn variant="primary" size="lg" type="submit" disabled={busy} style={{ width: '100%', justifyContent: 'center' }}>
          {busy ? 'Saving...' : 'Continue to portal'}
        </Btn>

        <button
          type="button"
          onClick={signOut}
          style={{
            margin: '16px auto 0',
            border: 'none',
            background: 'transparent',
            color: C.textSecondary,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            fontSize: 12.5,
            fontWeight: 700,
          }}
        >
          <LogOut size={14} />
          Not {email || 'you'}?
        </button>
      </form>
    </div>
  )
}
